import React from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AppShell } from "@/components/layout/AppShell";
import { HomePage } from "@/pages/Home";
import { CampaignsPage, CampaignDetailPage } from "@/pages/Campaigns";
import { CampaignEditorPage } from "@/pages/CampaignEditor";
import { MyCampaignsPage } from "@/pages/MyCampaigns";
import { DashboardPage } from "@/pages/Dashboard";
import { SettingsPage } from "@/pages/Settings";
import { SocialsPage } from "@/pages/Socials";
import { EarningsPage } from "@/pages/Earnings";
import { LeaderboardPage } from "@/pages/Leaderboard";
import { ModPanelPage } from "@/pages/Mod";
import ProfilePage from "@/pages/Profile";

import { LandingPage } from "@/pages/Landing";
import { CreatorPage } from "@/pages/Creator";
import { ContactPage } from "@/pages/Contact";
import { LoginPage } from "@/pages/Login";
import { PrivacyPage } from "@/pages/Privacy";
import { TermsPage } from "@/pages/Terms";
import { DataDeletionPage } from "@/pages/DataDeletion";

import { HelpPage } from "@/pages/Help";
import { AdminPage } from "@/pages/Admin";
import CampaignInsightsPage from "@/pages/CampaignInsights";
import { NotFoundPage } from "@/pages/NotFound";
import { DiscordCallbackPage } from "@/pages/DiscordCallback";
import { Toaster } from "@/components/ui/toaster";

const MARKETING_PATHS = new Set(["/", "/creator", "/contact", "/privacy", "/terms", "/data-deletion"]);

function AppLoading() {
  return (
    <div data-testid="app-loading" className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-4 py-14">
        <div data-testid="app-loading-text" className="rounded-2xl border border-border/60 bg-card/70 px-4 py-10 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      </div>
    </div>
  );
}

// Full logged-in app tree (AppShell + protected routes)
function ProtectedRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/login" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/new" element={<CampaignEditorPage />} />
        <Route path="/campaigns/:campaignId" element={<CampaignDetailPage />} />
        <Route path="/campaigns/:campaignId/edit" element={<CampaignEditorPage />} />
        <Route path="/my-campaigns" element={<MyCampaignsPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/earnings" element={<EarningsPage />} />
        <Route path="/wallet" element={<EarningsPage />} />
        <Route path="/socials" element={<SocialsPage />} />
        <Route path="/payouts" element={<EarningsPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/insights" element={<CampaignInsightsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/:adminTab" element={<AdminPage />} />
        <Route path="/mod" element={<ModPanelPage />} />
        <Route path="/mod/:modTab" element={<ModPanelPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AppShell>
  );
}

// SINGLE-ORIGIN ROUTER
// Everything lives on one domain (clippingbase.com):
// - /            → marketing landing (or /home if logged in)
// - /creator     → creator landing
// - /contact     → contact
// - /login       → full-page login (redirects to /home if already logged in)
// - /home, /dashboard, ...  → protected app (redirects to /login if not authed)
function HostRouter() {
  const { loading, me } = useAuth();
  const location = useLocation();
  if (loading) return <AppLoading />;

  const isMarketingPath = MARKETING_PATHS.has(location.pathname);

  if (!me) {
    // Unauthed: marketing pages public; /login public; everything else → /login
    if (isMarketingPath) {
      return (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/creator" element={<CreatorPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
        </Routes>
      );
    }
    if (location.pathname === "/login") return <LoginPage />;
    return <Navigate to="/login" replace />;
  }

  // Authed: /login → /home; marketing paths still visitable; everything else = app.
  if (location.pathname === "/login") return <Navigate to="/home" replace />;
  if (isMarketingPath) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/creator" element={<CreatorPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
      </Routes>
    );
  }
  return <ProtectedRoutes />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            {/* Discord callback is always available */}
            <Route path="/auth/discord/callback" element={<DiscordCallbackPage />} />
            <Route path="/*" element={<HostRouter />} />
          </Routes>
        </AuthProvider>
        <Toaster />
      </ThemeProvider>
    </BrowserRouter>
  );
}
